import { z } from 'zod';
import { isCountryCode } from './countries.js';

// Feed brand kit -> template brand props. Shared so the manager preview
// and the headless export build the same props from the same document.
export * from './feedBrand.js';
export * from './musicTracks.js';
export * from './scrapers.js';
export * from './countries.js';
export * from './demoData.js';

export const RoleSchema = z.enum(['user', 'admin', 'manager', 'agent']);
export type Role = z.infer<typeof RoleSchema>;

export const UserSchema = z.object({
  _id: z.string(),
  email: z.string().email(),
  name: z.string().optional(),
  image: z.string().nullable().optional(),
  role: RoleSchema,
  createdAt: z.string().or(z.date()).optional(),
  updatedAt: z.string().or(z.date()).optional(),
});
export type User = z.infer<typeof UserSchema>;

export const LoginInputSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8, 'min 8 characters'),
});
export type LoginInput = z.infer<typeof LoginInputSchema>;

// Which CRM the brokerage is currently using. Picked at signup so the
// onboarding wizard can route them to the right importer/integration. The
// known options match the legacy parsers (propspace / pixxi / goyzer /
// propertyfinder); the `other` branch carries a free-text label the user
// typed in. This is the *choice* only — credentials are collected at
// /onboarding/save-crm via SaveCrmInputSchema.
export const CrmChoiceSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('propspace') }),
  z.object({ type: z.literal('pixxi') }),
  z.object({ type: z.literal('goyzer') }),
  z.object({ type: z.literal('propertyfinder') }),
  z.object({
    type: z.literal('other'),
    name: z.string().min(2, 'tell us which CRM').max(100),
  }),
]);
export type CrmChoice = z.infer<typeof CrmChoiceSchema>;

// Accepts "acme.com", "www.acme.com/agents", or "https://acme.com" — rejects
// free text like "martin agency one" (no spaces, needs a dotted hostname with
// a plausible TLD). Kept permissive on purpose; the API normalises the value
// to "https://host" before storing.
export function isWebsiteLike(value: string): boolean {
  const v = value.trim();
  if (/\s/.test(v)) return false;
  const withoutScheme = v.replace(/^https?:\/\//i, '');
  const host = withoutScheme.split(/[/?#]/, 1)[0] ?? '';
  return /^([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}(:\d+)?$/i.test(host);
}

export const SignupInputSchema = LoginInputSchema.extend({
  // Company website doubles as the brokerage identifier and a soft proof-of-
  // intent signal. Required, but accept either bare-host ("acme.com") or full
  // URL ("https://acme.com") — the API normalises before storing.
  companyWebsite: z
    .string()
    .min(3, 'company website is required')
    .max(255)
    .refine(isWebsiteLike, 'enter a valid website, e.g. acme.com'),
  // CRM choice was collected at signup originally, but is now collected in
  // the onboarding wizard's CRM step instead — kept optional here for any
  // legacy clients still posting it.
  // ISO 3166-1 alpha-2 of the market the brokerage operates in, picked at
  // signup. Optional so older clients (and the API's own tests) keep working.
  country: z
    .string()
    .length(2)
    .toUpperCase()
    .refine(isCountryCode, 'unknown country code')
    .nullish(),
  // Property Finder broker id, picked from the scraped directory at signup.
  // Persisting it is what lets the one-off agency import know which brokerage
  // to pull (see apps/api/src/services/propertyFinder/scrapeAgency.ts).
  agencyPfId: z.number().int().positive().nullish(),
  crm: CrmChoiceSchema.optional(),
  // Where the verification email's magic link should land — the manager app's
  // /onboarding route. Sent by the client so we don't have to hardcode prod
  // hosts in the API.
  redirectUrl: z.string().url(),
  // Cloudflare Turnstile response. Checked by the API before the route runs,
  // and only when TURNSTILE_SECRET_KEY is set (apps/api/src/plugins/edge.ts).
  turnstileToken: z.string().max(2048).optional(),
});
export type SignupInput = z.infer<typeof SignupInputSchema>;

export const VerifySignupInputSchema = z.object({
  token: z.string().min(20),
});
export type VerifySignupInput = z.infer<typeof VerifySignupInputSchema>;

// Onboarding step 1 — what we extract from the brokerage's website via
// Firecrawl. All fields can come back null/empty when the page doesn't
// surface them, but the shape itself is always present.
export const ScrapedWebsiteSchema = z.object({
  sourceUrl: z.string(),
  companyName: z.string().nullable(),
  logo: z.string().nullable(),
  favicon: z.string().nullable(),
  description: z.string().nullable(),
  // Top brand-color candidates (hex) parsed from the page HTML. Up to 3,
  // ordered by frequency. Empty when the page only uses neutrals.
  colors: z.array(z.string()),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  locations: z.array(z.string()),
  services: z.array(z.string()),
  // ISO string when the API returns it; Date when we hold it locally.
  fetchedAt: z.string().or(z.date()),
});
export type ScrapedWebsite = z.infer<typeof ScrapedWebsiteSchema>;

export const ScrapeWebsiteInputSchema = z.object({
  // Optional override — when omitted, the API uses the user's
  // pendingCompanyWebsite from signup.
  url: z.string().min(3).optional(),
  // When true, ignore the cached pendingScrape on the user doc and
  // re-call Firecrawl. Used by the wizard's "re-fetch" button.
  force: z.boolean().optional(),
});
export type ScrapeWebsiteInput = z.infer<typeof ScrapeWebsiteInputSchema>;

// Captured on the agent-lead branch of step 1 — info about the agent's
// manager so a Proptr rep can reach out. The agent themselves never
// becomes an active manager-portal user, so these fields live on the
// enterprise (which is the brokerage we want to convert).
export const ManagerContactSchema = z.object({
  name: z.string().min(2, "enter your manager's name").max(100),
  email: z.string().email('enter a valid email').max(255),
  phone: z.string().max(64).optional(),
  companyName: z.string().min(2, 'company name required').max(255),
  role: z.string().min(2, "manager's role required").max(100),
});
export type ManagerContact = z.infer<typeof ManagerContactSchema>;

// Read by individual onboarding step components on mount so each step is
// self-contained (no need to plumb data through OnboardingPage from the
// verifySignup response, which only fires on the magic-link path).
// pendingApiKey/pendingApiSecret are only meaningful when pendingCrm.type
// is a credential-based CRM (currently: propertyfinder). null otherwise.
// pendingSignupKind/pendingWantsListings/pendingManagerContact track the
// step-1 (Role + listings) submission.
export const OnboardingStateSchema = z.object({
  pendingCompanyWebsite: z.string().nullable(),
  pendingCrm: CrmChoiceSchema.nullable(),
  pendingCrmSource: z.string().nullable(),
  pendingApiKey: z.string().nullable(),
  pendingApiSecret: z.string().nullable(),
  pendingSignupKind: z
    .union([z.literal('manager'), z.literal('agent-lead')])
    .nullable(),
  pendingWantsListings: z.boolean().nullable(),
  pendingManagerContact: ManagerContactSchema.nullable(),
});
export type OnboardingState = z.infer<typeof OnboardingStateSchema>;

// Step 1 (new) — the user picks their role and (if a manager) whether
// they want to surface listings on their proptrees. Agents drop into a
// lead-capture form for their manager's contact details and can't sign in
// after submitting; managers continue into the wizard.
export const SaveRoleInputSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('manager'),
    wantsListings: z.boolean(),
  }),
  z.object({
    kind: z.literal('agent-lead'),
    managerContact: ManagerContactSchema,
  }),
]);
export type SaveRoleInput = z.infer<typeof SaveRoleInputSchema>;

// Brand-basics step — persists the confirmed identity/contact details
// onto the enterprise. Logo + favicon are intentionally absent: the API
// copies them from the server-side cached scrape, never from the client.
export const SaveBrandInputSchema = z.object({
  companyName: z.string().trim().min(1).max(200).nullable(),
  description: z.string().trim().max(2000).nullable(),
  mainColor: z.string().trim().max(32).nullable(),
  secondaryColor: z.string().trim().max(32).nullable(),
  email: z.string().trim().email().nullable(),
  phone: z.string().trim().max(40).nullable(),
});
export type SaveBrandInput = z.infer<typeof SaveBrandInputSchema>;

// API response → the API tells the client which screen to show next.
// Keeps the branching server-authoritative so the wizard can stay dumb.
// 'agent-thank-you' implies the auth cookie has been cleared server-side.
export const SaveRoleResponseSchema = z.object({
  next: z.union([
    z.literal('brand'),
    z.literal('crm'),
    z.literal('dashboard'),
    z.literal('agent-thank-you'),
  ]),
});
export type SaveRoleResponse = z.infer<typeof SaveRoleResponseSchema>;

// Step 2 — connect CRM. Discriminated by `type` so each CRM declares the
// credentials it actually needs: feed-based CRMs (propspace, goyzer, pixxi)
// take a `sourceUrl`; Property Finder takes `apiKey` + `apiSecret` (issued
// from the PF Atlas dashboard); `other` is free-text + a source URL.
export const SaveCrmInputSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('propspace'),
    sourceUrl: z.string().min(3, 'enter a feed URL').max(2048),
  }),
  z.object({
    type: z.literal('pixxi'),
    sourceUrl: z.string().min(3, 'enter a feed URL').max(2048),
  }),
  z.object({
    type: z.literal('goyzer'),
    sourceUrl: z.string().min(3, 'enter a feed URL').max(2048),
  }),
  z.object({
    type: z.literal('propertyfinder'),
    apiKey: z.string().min(1, 'enter your API key').max(512),
    apiSecret: z.string().min(1, 'enter your API secret').max(512),
  }),
  z.object({
    type: z.literal('other'),
    name: z.string().min(2, 'tell us which CRM').max(100),
    sourceUrl: z.string().min(3, 'enter a feed URL').max(2048),
  }),
]);
export type SaveCrmInput = z.infer<typeof SaveCrmInputSchema>;

export const EnterpriseSchema = z.object({
  _id: z.string(),
  name: z.string().optional().nullable(),
  logo: z.string().optional().nullable(),
  favicon: z.string().optional().nullable(),
  primaryColor: z.string().optional().nullable(),
  secondaryColor: z.string().optional().nullable(),
  // Whether the brokerage syncs a CRM at all. Missing (legacy docs) means
  // true. false grays the CRM-connection + lead-management settings and
  // hides the listing-driven tabs (Agents/Listings/Proptree).
  crmSyncEnabled: z.boolean().optional().nullable(),
  // True once a manager finished the first-arrival welcome tour.
  welcomeTourDone: z.boolean().optional().nullable(),
  // Custom feed parsers this agency may pick in CRM settings.
  customParsers: z.array(z.string()).optional(),
});

// --- Profile (per-user) & Agency settings (per-enterprise) -----------

export const UpdateProfileInputSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  image: z.string().max(2048).nullable().optional(),
});
export type UpdateProfileInput = z.infer<typeof UpdateProfileInputSchema>;

export const ChangePasswordInputSchema = z.object({
  currentPassword: z.string().min(1, 'current password required'),
  newPassword: z.string().min(8, 'minimum 8 characters').max(200),
});
export type ChangePasswordInput = z.infer<typeof ChangePasswordInputSchema>;

// Manager-editable agency settings — the self-service subset (the admin
// portal's EnterpriseSettingsUpdateInput stays admin-only).
export const AgencySettingsSchema = z.object({
  name: z.string().nullable(),
  companyName: z.string().nullable(),
  companyWebsite: z.string().nullable(),
  companyBio: z.string().nullable(),
  logo: z.string().nullable(),
  favicon: z.string().nullable(),
  primaryColor: z.string().nullable(),
  secondaryColor: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
});
export type AgencySettings = z.infer<typeof AgencySettingsSchema>;

export const AgencySettingsInputSchema = z.object({
  name: z.string().trim().max(200).nullable().optional(),
  companyName: z.string().trim().max(200).nullable().optional(),
  companyWebsite: z.string().trim().max(2048).nullable().optional(),
  companyBio: z.string().trim().max(2000).nullable().optional(),
  logo: z.string().max(2048).nullable().optional(),
  favicon: z.string().max(2048).nullable().optional(),
  primaryColor: z.string().trim().max(32).nullable().optional(),
  secondaryColor: z.string().trim().max(32).nullable().optional(),
  email: z.string().trim().max(320).nullable().optional(),
  phone: z.string().trim().max(40).nullable().optional(),
});
export type AgencySettingsInput = z.infer<typeof AgencySettingsInputSchema>;

export const CrmSyncInputSchema = z.object({ enabled: z.boolean() });
export type CrmSyncInput = z.infer<typeof CrmSyncInputSchema>;
export type Enterprise = z.infer<typeof EnterpriseSchema>;

export const AuthResponseSchema = z.object({
  user: UserSchema,
  enterprise: EnterpriseSchema.nullable().optional(),
});
export type AuthResponse = z.infer<typeof AuthResponseSchema>;

export const ForgotPasswordInputSchema = z.object({
  email: z.string().email(),
  redirectUrl: z.string().url(),
});
export type ForgotPasswordInput = z.infer<typeof ForgotPasswordInputSchema>;

export const ResetPasswordInputSchema = z.object({
  token: z.string().min(20),
  password: z.string().min(8, 'min 8 characters'),
});
export type ResetPasswordInput = z.infer<typeof ResetPasswordInputSchema>;

// Admin login is two-step: first POST email+password to /auth/admin/login,
// which (on success) emails a 6-digit OTP and returns a short-lived
// `otpToken` JWT. Then POST that token + the code to /auth/admin/verify-otp
// to receive the full auth cookie.
export const AdminLoginInputSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8, 'min 8 characters'),
  // Cloudflare Turnstile response — see SignupInputSchema.turnstileToken.
  turnstileToken: z.string().max(2048).optional(),
});
export type AdminLoginInput = z.infer<typeof AdminLoginInputSchema>;

export const AdminLoginResponseSchema = z.object({
  otpToken: z.string(),
  // Display hint so the admin knows which inbox to check; never the full
  // address. Format: "m****@example.com".
  maskedEmail: z.string(),
});
export type AdminLoginResponse = z.infer<typeof AdminLoginResponseSchema>;

export const AdminOtpVerifyInputSchema = z.object({
  otpToken: z.string().min(20),
  code: z.string().regex(/^\d{6}$/, '6-digit code required'),
});
export type AdminOtpVerifyInput = z.infer<typeof AdminOtpVerifyInputSchema>;

// Admin → list of enterprises ("agencies" in the admin UI). One row per
// enterprise with summary counts (managers, proptrees, listings) so the
// admin can see scale at a glance without drilling in.
export const EnterpriseListItemSchema = z.object({
  _id: z.string(),
  name: z.string().nullable(),
  companyName: z.string().nullable(),
  logo: z.string().nullable(),
  market: z.string().nullable(),
  currency: z.string().nullable(),
  legacy: z.boolean(),
  createdAt: z.string().or(z.date()).optional().nullable(),
  usersCount: z.number(),
  managersCount: z.number(),
  proptreesCount: z.number(),
  listingsCount: z.number(),
});
export type EnterpriseListItem = z.infer<typeof EnterpriseListItemSchema>;

// Legacy `{ id, name, value, ...extras }` enum shape used across the
// enterprise document. `extras` covers the `capitalize` flag on alias.
export const EnterpriseEnumValueSchema = z
  .object({
    id: z.number(),
    name: z.string(),
    value: z.string(),
  })
  .catchall(z.unknown());
export type EnterpriseEnumValue = z.infer<typeof EnterpriseEnumValueSchema>;

// Standalone shape — list keeps flattened strings (market/currency display
// names), detail returns the raw enum objects so the form can edit them.
export const EnterpriseDetailSchema = z.object({
  _id: z.string(),
  name: z.string().nullable(),
  companyName: z.string().nullable(),
  logo: z.string().nullable(),
  favicon: z.string().nullable(),
  themeImage: z.string().nullable(),
  legacy: z.boolean(),
  createdAt: z.string().or(z.date()).optional().nullable(),

  // Counts
  usersCount: z.number(),
  managersCount: z.number(),
  proptreesCount: z.number(),
  listingsCount: z.number(),

  // Switches (mirrors enterprise.proptreeSwitch.value === 'enabled')
  proptreeSwitch: z.boolean(),

  // Enterprise basics
  parserId: z.string().nullable(),
  email: z.string().nullable(),
  companyWebsite: z.string().nullable(),
  companyBio: z.string().nullable(),
  status: EnterpriseEnumValueSchema.nullable(),
  market: EnterpriseEnumValueSchema.nullable(),
  timeZone: EnterpriseEnumValueSchema.nullable(),
  autoAppendWebsiteUtm: EnterpriseEnumValueSchema.nullable(),

  // Branding / details
  adboosterId: z.string().nullable(),
  adboosterType: EnterpriseEnumValueSchema.nullable(),
  primaryColor: z.string().nullable(),
  secondaryColor: z.string().nullable(),
  startYearBenchmark: EnterpriseEnumValueSchema.nullable(),
  startMonthBenchmark: EnterpriseEnumValueSchema.nullable(),

  // Pixels (Meta only — legacy=enabled flow)
  facebookPixelIdLight: z.string().nullable(),
  facebookConversionPixelIdLight: z.string().nullable(),

  // Datasources / parser
  parserType: EnterpriseEnumValueSchema.nullable(),
  xmlUrl: z.string().nullable(),
  xmlFeeds: z
    .array(
      z.object({
        id: z.union([z.string(), z.number()]),
        url: z.string(),
        updated: z.string().or(z.date()).optional().nullable(),
      }),
    )
    .nullable(),
  apiKey: z.string().nullable(),
  apiSecret: z.string().nullable(),
  apiToken: z.string().nullable(),
  kendalUserID: z.string().nullable(),
  kendalOrgID: z.string().nullable(),
  propfusionURL: z.string().nullable(),
  goyzerAccessCode: z.string().nullable(),
  goyzerGroupCode: z.string().nullable(),
  goyzerMediaName: z.string().nullable(),
  goyzerMethodOfContact: z.string().nullable(),
  goyzerFallbackBrokerID: z.string().nullable(),
  qrCodeMethod: EnterpriseEnumValueSchema.nullable(),
  pixxiListingStatusInclude: EnterpriseEnumValueSchema.nullable(),
  kendalListingStatusInclude: EnterpriseEnumValueSchema.nullable(),
  propfusionListingStatusInclude: EnterpriseEnumValueSchema.nullable(),
  propertyFinderListingStatusInclude: EnterpriseEnumValueSchema.nullable(),
  pixxiFormType: EnterpriseEnumValueSchema.nullable(),
  formId: z.string().nullable(),

  // CRM notifications
  zapierPush: EnterpriseEnumValueSchema.nullable(),
  zapierEndpoint: z.string().nullable(),

  // Custom feed parsers enabled for the agency; null = never set (the
  // legacy datasource's own parser applies — see allowedCustomParsers).
  customParsers: z.array(z.string()).nullable(),

  // Healthcheck thresholds
  premiumSalesPriceHealthcheck: z.number().nullable(),
  premiumRentalPriceHealthcheck: z.number().nullable(),
  minimumActiveListingsHealthcheck: z.number().nullable(),
  minimumListingSpendHealthcheck: z.number().nullable(),
  channelsHealthcheck: EnterpriseEnumValueSchema.nullable(),
  advertisementSpend: z.number().nullable(),

  // Proptree settings
  autoCreateProptree: EnterpriseEnumValueSchema.nullable(),
  autoSubscribeProptreeAgent: EnterpriseEnumValueSchema.nullable(),
  autoAliasProptree: EnterpriseEnumValueSchema.nullable(),
  proptreeExtraEnterprise: EnterpriseEnumValueSchema.nullable(),
  autoSendProptreeWelcome: EnterpriseEnumValueSchema.nullable(),
  autoSendProptreeWelcomeEmail: EnterpriseEnumValueSchema.nullable(),
  autoSendProptreeTransfer: EnterpriseEnumValueSchema.nullable(),
  autoSendProptreeDisable: EnterpriseEnumValueSchema.nullable(),
});
export type EnterpriseDetail = z.infer<typeof EnterpriseDetailSchema>;

// PATCH body — every field optional. `null` clears via $unset, otherwise the
// value is written as-is. The schema accepts both string/number primitives
// and the legacy enum object shape, depending on the field.
const optionalEnum = EnterpriseEnumValueSchema.nullable().optional();
const optionalString = z.string().nullable().optional();
const optionalNumber = z.number().nullable().optional();
export const EnterpriseSettingsUpdateInputSchema = z.object({
  // Basics
  name: optionalString,
  companyName: optionalString,
  parserId: optionalString,
  email: optionalString,
  companyWebsite: optionalString,
  companyBio: optionalString,
  status: optionalEnum,
  market: optionalEnum,
  timeZone: optionalEnum,
  autoAppendWebsiteUtm: optionalEnum,
  // Details
  adboosterId: optionalString,
  adboosterType: optionalEnum,
  primaryColor: optionalString,
  secondaryColor: optionalString,
  startYearBenchmark: optionalEnum,
  startMonthBenchmark: optionalEnum,
  facebookPixelIdLight: optionalString,
  facebookConversionPixelIdLight: optionalString,
  premiumSalesPriceHealthcheck: optionalNumber,
  premiumRentalPriceHealthcheck: optionalNumber,
  minimumActiveListingsHealthcheck: optionalNumber,
  minimumListingSpendHealthcheck: optionalNumber,
  channelsHealthcheck: optionalEnum,
  advertisementSpend: optionalNumber,
  // Media
  logo: optionalString,
  favicon: optionalString,
  themeImage: optionalString,
  // Datasources
  parserType: optionalEnum,
  xmlUrl: optionalString,
  xmlFeeds: z
    .array(
      z.object({
        id: z.union([z.string(), z.number()]),
        url: z.string(),
        updated: z.string().or(z.date()).optional().nullable(),
      }),
    )
    .nullable()
    .optional(),
  apiKey: optionalString,
  apiSecret: optionalString,
  apiToken: optionalString,
  kendalUserID: optionalString,
  kendalOrgID: optionalString,
  propfusionURL: optionalString,
  goyzerAccessCode: optionalString,
  goyzerGroupCode: optionalString,
  goyzerMediaName: optionalString,
  goyzerMethodOfContact: optionalString,
  goyzerFallbackBrokerID: optionalString,
  qrCodeMethod: optionalEnum,
  pixxiListingStatusInclude: optionalEnum,
  kendalListingStatusInclude: optionalEnum,
  propfusionListingStatusInclude: optionalEnum,
  propertyFinderListingStatusInclude: optionalEnum,
  pixxiFormType: optionalEnum,
  formId: optionalString,
  // CRM notifications
  zapierPush: optionalEnum,
  zapierEndpoint: optionalString,
  // Custom feed parsers (CUSTOM_PARSERS keys); null resets to the default.
  customParsers: z.array(z.string().max(64)).max(20).nullable().optional(),
  // Proptree settings
  autoCreateProptree: optionalEnum,
  autoSubscribeProptreeAgent: optionalEnum,
  autoAliasProptree: optionalEnum,
  proptreeExtraEnterprise: optionalEnum,
  autoSendProptreeWelcome: optionalEnum,
  autoSendProptreeWelcomeEmail: optionalEnum,
  autoSendProptreeTransfer: optionalEnum,
  autoSendProptreeDisable: optionalEnum,
});
export type EnterpriseSettingsUpdateInput = z.infer<
  typeof EnterpriseSettingsUpdateInputSchema
>;

export const EnterpriseListResponseSchema = z.object({
  items: z.array(EnterpriseListItemSchema),
  meta: z.object({
    total: z.number(),
    page: z.number(),
    limit: z.number(),
    pages: z.number(),
  }),
});
export type EnterpriseListResponse = z.infer<
  typeof EnterpriseListResponseSchema
>;

export const EnterpriseListQuerySchema = z.object({
  search: z.string().optional(),
  page: z.number().int().positive().optional(),
  limit: z.number().int().positive().optional(),
});
export type EnterpriseListQuery = z.infer<typeof EnterpriseListQuerySchema>;

export const StatBucketSchema = z.object({
  thisMonth: z.number(),
  prevMonth: z.number(),
  sinceStart: z.number(),
  deltaPct: z.number().nullable(),
});
export type StatBucket = z.infer<typeof StatBucketSchema>;

export const ChannelCountSchema = z.object({
  name: z.string(),
  thisMonth: z.number(),
  sinceStart: z.number(),
});
export type ChannelCount = z.infer<typeof ChannelCountSchema>;

export const TopAgentSchema = z.object({
  id: z.string(),
  name: z.string(),
  alias: z.string(),
  image: z.string().nullable(),
  clicks: z.number(),
  leads: z.number(),
});
export type TopAgent = z.infer<typeof TopAgentSchema>;

export const TopButtonSchema = z.object({
  id: z.string(),
  title: z.string(),
  url: z.string(),
  // ProfileButton type ('link' | 'video' | 'gallery' | 'whatsapp') so the
  // dashboard can show a matching glyph. Null when the button no longer
  // exists in profileButtons (clicks outlive deleted buttons).
  type: z.string().nullable().optional(),
  clicks: z.number(),
});
export type TopButton = z.infer<typeof TopButtonSchema>;

export const DashboardStatsSchema = z.object({
  month: z.string(),
  prevMonth: z.string(),
  views: StatBucketSchema,
  clicks: StatBucketSchema,
  inquiries: StatBucketSchema,
  channels: z.array(ChannelCountSchema),
  topAgents: z.array(TopAgentSchema),
  topButtons: z.array(TopButtonSchema),
});
export type DashboardStats = z.infer<typeof DashboardStatsSchema>;

export const AccessLevelSchema = z.object({
  id: z.number(),
  name: z.string().optional(),
});
export const StatusLevelSchema = z.object({
  id: z.number(),
  name: z.string().optional(),
});

// Set on /api/auth/me when the current JWT carries `impersonatedBy`. Lets
// the manager portal show a "Logged in as X / originally Y" banner.
export const ImpersonatorSchema = z.object({
  _id: z.string(),
  name: z.string().optional(),
  email: z.string(),
});
export type Impersonator = z.infer<typeof ImpersonatorSchema>;

// POST /api/auth/admin/login-as
export const LoginAsInputSchema = z.object({
  userId: z.string(),
});
export type LoginAsInput = z.infer<typeof LoginAsInputSchema>;

export const LoginAsResponseSchema = z.object({
  impToken: z.string(),
  managerName: z.string().nullable(),
});
export type LoginAsResponse = z.infer<typeof LoginAsResponseSchema>;

// POST /api/auth/manager/login-as-agent — same input shape as the
// admin → manager flow, but the JWT it mints has `role: 'agent'` and
// `impersonatedBy: <manager id>`. The manager's enterprise must own the
// target agent (the route validates this).
export const LoginAsAgentInputSchema = z.object({
  userId: z.string(),
});
export type LoginAsAgentInput = z.infer<typeof LoginAsAgentInputSchema>;

export const LoginAsAgentResponseSchema = z.object({
  impToken: z.string(),
  agentName: z.string().nullable(),
});
export type LoginAsAgentResponse = z.infer<typeof LoginAsAgentResponseSchema>;

// Manager users in an agency — admin-only listing returned by
// GET /api/admin/enterprises/:id/users. Filtered to `access.id === 1`.
export const AgencyUserListItemSchema = z.object({
  _id: z.string(),
  name: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  image: z.string().nullable(),
  access: AccessLevelSchema.nullable(),
  status: StatusLevelSchema.nullable(),
  createdAt: z.union([z.string(), z.date()]).nullable(),
});
export type AgencyUserListItem = z.infer<typeof AgencyUserListItemSchema>;

export const AgencyUserListResponseSchema = z.object({
  items: z.array(AgencyUserListItemSchema),
  meta: z.object({
    total: z.number(),
  }),
});
export type AgencyUserListResponse = z.infer<typeof AgencyUserListResponseSchema>;

export const AgentSchema = z.object({
  _id: z.string(),
  name: z.string().optional().nullable(),
  email: z.string().optional().nullable(),
  phone: z.string().optional().nullable(),
  license: z.string().optional().nullable(),
  image: z.string().optional().nullable(),
  access: AccessLevelSchema.optional().nullable(),
  status: StatusLevelSchema.optional().nullable(),
  whatsapp: z.unknown().optional(),
  proptree: z
    .object({
      _id: z.string().optional(),
      alias: z.string().optional().nullable(),
      shortId: z.string().optional().nullable(),
    })
    .nullable(),
  totalLeads: z.number(),
  totalListings: z.number(),
  // How the user record came into existence — 'manual' = added by a
  // manager, 'sync' = auto-created by the listing-sync parser, 'system'
  // = brokerage placeholder. Missing for legacy rows (display as 'sync').
  source: z.enum(['manual', 'sync', 'system']).optional().nullable(),
  createdAt: z.union([z.string(), z.date()]).optional().nullable(),
});
export type Agent = z.infer<typeof AgentSchema>;

export const AgentListResponseSchema = z.object({
  items: z.array(AgentSchema),
  meta: z.object({
    total: z.number(),
    active: z.number(),
    disabled: z.number(),
    page: z.number(),
    limit: z.number(),
    pages: z.number(),
  }),
});
export type AgentListResponse = z.infer<typeof AgentListResponseSchema>;

export const AgentListQuerySchema = z.object({
  status: z.enum(['active', 'disabled', 'all']).optional(),
  proptree: z.enum(['has', 'none', 'all']).optional(),
  sort: z.enum(['name', 'totalLeads', 'totalListings', 'createdAt']).optional(),
  order: z.enum(['asc', 'desc']).optional(),
  page: z.number().int().positive().optional(),
  limit: z.number().int().positive().optional(),
});
export type AgentListQuery = z.infer<typeof AgentListQuerySchema>;

// Manager-created agent. Email + name are required (email is the unique
// per-enterprise key); phone/license/image are optional and can be set
// later. The server tags the new user with access=Regular/level=Agent/
// status=Active and binds them to the caller's enterprise.
export const AgentCreateInputSchema = z.object({
  name: z.string().trim().min(2, 'name required').max(120),
  email: z.string().trim().toLowerCase().email('valid email required').max(320),
  phone: z.string().trim().max(40).optional(),
  license: z.string().trim().max(120).optional(),
  image: z.string().trim().url('must be a URL').max(2048).optional(),
});
export type AgentCreateInput = z.infer<typeof AgentCreateInputSchema>;

export const AgentCreateResponseSchema = z.object({
  _id: z.string(),
  name: z.string(),
  email: z.string(),
  phone: z.string().optional(),
  license: z.string().optional(),
  image: z.string().optional(),
});
export type AgentCreateResponse = z.infer<typeof AgentCreateResponseSchema>;

export const ListingRowSchema = z.object({
  _id: z.string(),
  externalId: z.string().optional().nullable(),
  permitId: z.string().optional().nullable(),
  /** Hosted PNG of the Trakheesi permit QR. Dubai requires it on
   *  advertised creative, so templates render it when present. */
  permitQr: z.string().optional().nullable(),
  title: z.string().optional().nullable(),
  image: z.string().optional().nullable(),
  /** Photos after the hero, for the 9:16 creative's gallery rail. The
   *  rail needs three; fewer and the creative falls back to full-bleed. */
  gallery: z.array(z.string()).optional().nullable(),
  bedrooms: z.number().optional().nullable(),
  bathrooms: z.number().optional().nullable(),
  price: z.number().optional().nullable(),
  currency: z.string().optional().nullable(),
  forSale: z.boolean().optional(),
  forRent: z.boolean().optional(),
  propertyType: z.string().optional().nullable(),
  area: z.string().optional().nullable(),
  city: z.string().optional().nullable(),
  brokerId: z.string().optional().nullable(),
  agent: z
    .object({
      _id: z.string(),
      name: z.string().optional().nullable(),
      email: z.string().optional().nullable(),
      image: z.string().optional().nullable(),
    })
    .nullable()
    .optional(),
  listingStatus: z.string().optional().nullable(),
  internalCreated: z.union([z.string(), z.date()]).optional().nullable(),
  totalLeads: z.number(),
});
export type ListingRow = z.infer<typeof ListingRowSchema>;

export const ListingListResponseSchema = z.object({
  items: z.array(ListingRowSchema),
  meta: z.object({
    total: z.number(),
    active: z.number(),
    sale: z.number(),
    rent: z.number(),
    archived: z.number(),
    page: z.number(),
    limit: z.number(),
    pages: z.number(),
  }),
});
export type ListingListResponse = z.infer<typeof ListingListResponseSchema>;

export const ListingListQuerySchema = z.object({
  status: z.enum(['active', 'archived']).optional(),
  type: z.enum(['sale', 'rent', 'all']).optional(),
  sort: z
    .enum(['totalLeads', 'price', 'bedrooms', 'bathrooms', 'created', 'reference'])
    .optional(),
  order: z.enum(['asc', 'desc']).optional(),
  page: z.number().int().positive().optional(),
  limit: z.number().int().positive().optional(),
  // Comma-separated externalIds for thumbnail/detail lookups. Bypasses
  // the status filter so archived picks still resolve.
  externalIds: z.string().optional(),
});
export type ListingListQuery = z.infer<typeof ListingListQuerySchema>;

export const TaggedValueSchema = z.object({
  id: z.number().optional(),
  name: z.string().optional(),
  value: z.string().optional(),
});

export const InquirySchema = z.object({
  _id: z.string(),
  createdAt: z.union([z.string(), z.date()]).optional().nullable(),
  channel: z.string().optional().nullable(),
  type: TaggedValueSchema.optional().nullable(),
  rating: TaggedValueSchema.optional().nullable(),
  followUp: TaggedValueSchema.optional().nullable(),
  status: TaggedValueSchema.optional().nullable(),
  listingExternalId: z.string().optional().nullable(),
  formId: z.string().optional().nullable(),
  formQuestionCount: z.number().optional(),
  // Legacy shape: object keyed by formId → object keyed by questionId →
  // { id, question, answer, correct_option, date }, with extra 'completed' /
  // 'started' sibling booleans we filter out on display.
  answeredQuestions: z.unknown().optional(),
  client: z
    .object({
      _id: z.string().optional().nullable(),
      name: z.string().optional().nullable(),
      email: z.string().optional().nullable(),
      phone: z.string().optional().nullable(),
      image: z.string().optional().nullable(),
      whatsapp: z.unknown().optional(),
    })
    .nullable(),
  agent: z
    .object({
      _id: z.string().optional().nullable(),
      name: z.string().optional().nullable(),
      image: z.string().optional().nullable(),
    })
    .nullable(),
  listing: z
    .object({
      _id: z.string().optional(),
      externalId: z.string().optional().nullable(),
      name: z.string().optional().nullable(),
      projectName: z.string().optional().nullable(),
    })
    .nullable(),
  proptreeOrigin: z
    .object({
      shortId: z.string().optional().nullable(),
      alias: z.string().optional().nullable(),
      profileFirstName: z.string().optional().nullable(),
      profileImage: z.string().optional().nullable(),
    })
    .nullable(),
});
export type Inquiry = z.infer<typeof InquirySchema>;

export const InquiryListResponseSchema = z.object({
  items: z.array(InquirySchema),
  meta: z.object({
    total: z.number(),
    all: z.number(),
    qualifications: z.object({
      relevant: z.number(),
      'double-qualified': z.number(),
      qualified: z.number(),
      'pre-qualified': z.number(),
      unqualified: z.number(),
    }),
    ratings: z.object({
      signed_deal: z.number(),
      high: z.number(),
      medium: z.number(),
      low: z.number(),
      block: z.number(),
      unrated: z.number(),
    }),
    followUps: z.object({
      followed_up: z.number(),
      pending_follow_up: z.number(),
      not_followed_up: z.number(),
    }),
    sources: z.object({
      listing: z.number(),
      button: z.number(),
    }),
    page: z.number(),
    limit: z.number(),
    pages: z.number(),
  }),
});
export type InquiryListResponse = z.infer<typeof InquiryListResponseSchema>;

export const InquiryListQuerySchema = z.object({
  qualification: z
    .enum([
      'all',
      'relevant',
      'double-qualified',
      'qualified',
      'pre-qualified',
      'unqualified',
    ])
    .optional(),
  rating: z
    .enum(['all', 'signed_deal', 'high', 'medium', 'low', 'block', 'unrated'])
    .optional(),
  followUp: z
    .enum(['all', 'followed_up', 'pending_follow_up', 'not_followed_up'])
    .optional(),
  agent: z.string().optional(),
  listing: z.string().optional(),
  client: z.string().optional(),
  start: z.string().optional(),
  end: z.string().optional(),
  sort: z.enum(['createdAt', 'name']).optional(),
  order: z.enum(['asc', 'desc']).optional(),
  page: z.number().int().positive().optional(),
  limit: z.number().int().positive().optional(),
});
export type InquiryListQuery = z.infer<typeof InquiryListQuerySchema>;

export const ClientLightSchema = z.object({
  _id: z.string(),
  name: z.string().optional().nullable(),
  email: z.string().optional().nullable(),
  phone: z.string().optional().nullable(),
  image: z.string().optional().nullable(),
  createdAt: z.string().or(z.date()).optional().nullable(),
  latestInquiry: z.string().or(z.date()).optional().nullable(),
  totalInquiries: z.number(),
});
export type ClientLight = z.infer<typeof ClientLightSchema>;

export const ClientListLightResponseSchema = z.object({
  items: z.array(ClientLightSchema),
  meta: z.object({
    total: z.number(),
    page: z.number(),
    limit: z.number(),
    pages: z.number(),
  }),
});
export type ClientListLightResponse = z.infer<
  typeof ClientListLightResponseSchema
>;

export const ClientListLightQuerySchema = z.object({
  sort: z
    .enum(['name', 'latestInquiry', 'totalInquiries', 'createdAt'])
    .optional(),
  order: z.enum(['asc', 'desc']).optional(),
  page: z.number().int().positive().optional(),
  limit: z.number().int().positive().optional(),
});
export type ClientListLightQuery = z.infer<typeof ClientListLightQuerySchema>;

export const ProptreeAgentSchema = z.object({
  _id: z.string().optional().nullable(),
  name: z.string().optional().nullable(),
  email: z.string().optional().nullable(),
  phone: z.string().optional().nullable(),
  image: z.string().optional().nullable(),
  statusName: z.string().optional().nullable(),
  recoveryCode: z.union([z.string(), z.number()]).optional().nullable(),
  linkInBio: z.unknown().optional().nullable(),
  welcomeWaAmount: z.number().optional().nullable(),
  welcomeEmailAmount: z.number().optional().nullable(),
  adminStatus: z.unknown().optional().nullable(),
  welcomeStatus: z.unknown().optional().nullable(),
  reminderStatus: z.unknown().optional().nullable(),
  reportStatus: z.unknown().optional().nullable(),
});
export type ProptreeAgent = z.infer<typeof ProptreeAgentSchema>;

export const ProptreeWithAgentSchema = z.object({
  _id: z.string(),
  shortId: z.string().optional().nullable(),
  alias: z.string().optional().nullable(),
  type: z.string().optional().nullable(),
  status: z.string().optional().nullable(),
  transferred: z.boolean().optional().nullable(),
  pending: z.boolean().optional().nullable(),
  reminderNotificationDisabled: z.boolean().optional().nullable(),
  reportNotificationDisabled: z.boolean().optional().nullable(),
  profileFirstName: z.string().optional().nullable(),
  profileImage: z.string().optional().nullable(),
  agencyDefault: z.boolean().optional().nullable(),
  createdAt: z.string().or(z.date()).optional().nullable(),
  agent: ProptreeAgentSchema.nullable(),
  inquiries: z.number(),
  listingInquiries: z.number(),
  activeListings: z.number(),
  pageViews: z.number(),
  listingClicks: z.number(),
  buttonClicks: z.number(),
  activeButtons: z.number(),
});
export type ProptreeWithAgent = z.infer<typeof ProptreeWithAgentSchema>;

// Affiliate proptrees — `type: 'custom'` with `agencyDefault !== true`.
// Returned by GET /api/proptrees/affiliates. Lighter shape than the agent
// proptree row because there's no joined agent / no welcome/reminder/report
// notification status.
export const AffiliateRowSchema = z.object({
  _id: z.string(),
  alias: z.string().nullable(),
  shortId: z.string().nullable(),
  status: z.string().nullable(),
  profileImage: z.string().nullable(),
  profileFirstName: z.string().nullable(),
  profileSurName: z.string().nullable(),
  profileCompany: z.string().nullable(),
  // Channels the page's link is confirmed to live on, detected from
  // per-channel organic page loads (same as the proptrees list).
  linkInBio: z.array(z.string()).optional().nullable(),
  buttonsCount: z.number(),
  activeButtons: z.number(),
  pageViews: z.number(),
  buttonClicks: z.number(),
  referralClicks: z.number(),
  createdAt: z.union([z.string(), z.date()]).nullable(),
});
export type AffiliateRow = z.infer<typeof AffiliateRowSchema>;

export const AffiliateListResponseSchema = z.object({
  items: z.array(AffiliateRowSchema),
  meta: z.object({
    total: z.number(),
    page: z.number(),
    limit: z.number(),
    pages: z.number(),
    counts: z.object({
      all: z.number(),
      enabled: z.number(),
      disabled: z.number(),
    }),
    // Legacy `enterprise.limitProptreeAmount.value` — the cap on enabled
    // affiliates. `null` when no quota is configured on the enterprise.
    quota: z.number().nullable(),
  }),
});
export type AffiliateListResponse = z.infer<
  typeof AffiliateListResponseSchema
>;

export const AffiliateListQuerySchema = z.object({
  status: z.enum(['all', 'enabled', 'disabled']).optional(),
  sort: z.enum(['createdAt', 'alias']).optional(),
  order: z.enum(['asc', 'desc']).optional(),
  page: z.number().int().positive().optional(),
  limit: z.number().int().positive().optional(),
});
export type AffiliateListQuery = z.infer<typeof AffiliateListQuerySchema>;

// Agency-default proptree settings (the doc with type=custom and
// agencyDefault=true). Read by the manager portal's
// /proptree/agency settings view. Field names mirror legacy
// enterprise-app/src/components/createNewProptree.js verbatim so the same
// dropdown semantics apply.
export const ProptreeEnumValueSchema = z.object({
  id: z.union([z.number(), z.string()]).optional(),
  name: z.string().optional(),
  value: z.union([z.string(), z.number(), z.boolean()]).optional(),
});
export type ProptreeEnumValue = z.infer<typeof ProptreeEnumValueSchema>;

export const AgencyProptreeSettingsSchema = z.object({
  _id: z.string(),
  enterprise: z.string().nullable(),
  alias: z.string().nullable(),
  shortId: z.string().nullable(),
  profileImage: z.string().nullable(),
  profileFirstName: z.string().nullable(),
  profileCompany: z.string().nullable(),
  agencyDefault: z.boolean(),
  externalAccess: ProptreeEnumValueSchema.nullable(),
  whatsAppCountryCode: ProptreeEnumValueSchema.nullable(),
  whatsAppNumber: z.string().nullable(),
  mainFeed: ProptreeEnumValueSchema.nullable(),
  propertiesType: ProptreeEnumValueSchema.nullable(),
  property: ProptreeEnumValueSchema.nullable(),
  showAllAgencyListings: ProptreeEnumValueSchema.nullable(),
  agency: z
    .object({
      showAgencyBanner: ProptreeEnumValueSchema.nullable(),
      agencyBannerStats: ProptreeEnumValueSchema.nullable(),
      showAgentFeed: ProptreeEnumValueSchema.nullable(),
    })
    .nullable(),
  feed: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .nullable(),
  // Custom action buttons rendered on the public agency proptree page,
  // same shape as the agent-proptree buttons. `z.lazy` defers binding
  // to ProfileButtonSchema (declared lower in this file).
  buttons: z.array(z.lazy(() => ProfileButtonSchema)),
  // Top-marquee slogan config. The update endpoint already accepts this;
  // returning it here lets the editor show the saved state instead of
  // always falling back to "off".
  bannerSlogans: z
    .lazy(() => BannerSlogansSchema)
    .optional()
    .nullable(),
  // Toggle for the "Stay in the loop" newsletter signup card. Off until
  // the manager opts in.
  newsletter: z.boolean().optional().nullable(),
  // Card copy. `null` renders NEWSLETTER_DEFAULT_TITLE / _SUBTITLE.
  newsletterTitle: z.string().optional().nullable(),
  newsletterSubtitle: z.string().optional().nullable(),
});
export type AgencyProptreeSettings = z.infer<
  typeof AgencyProptreeSettingsSchema
>;

// "Stay in the loop" newsletter card copy, used whenever a proptree
// hasn't customised its title / subtitle (stored as null).
export const NEWSLETTER_DEFAULT_TITLE = 'Stay in the loop';
export const NEWSLETTER_DEFAULT_SUBTITLE =
  'Get new listings and market updates delivered straight to your inbox.';
export const NEWSLETTER_TITLE_MAX = 60;
export const NEWSLETTER_SUBTITLE_MAX = 160;

// Partial-update payload for PUT /api/proptrees/agency. Only the keys
// the caller sends are written; empty / null copy falls back to the
// defaults above.
export const AgencyProptreeSettingsUpdateInputSchema = z.object({
  alias: z
    .string()
    .regex(/^[a-z0-9-]*$/, 'alias may only contain lowercase letters, digits, and dashes')
    .max(60)
    .optional(),
  newsletter: z.boolean().optional(),
  newsletterTitle: z.string().max(NEWSLETTER_TITLE_MAX).nullable().optional(),
  newsletterSubtitle: z
    .string()
    .max(NEWSLETTER_SUBTITLE_MAX)
    .nullable()
    .optional(),
});
export type AgencyProptreeSettingsUpdateInput = z.infer<
  typeof AgencyProptreeSettingsUpdateInputSchema
>;

// Top-marquee config shared by every proptree-settings surface. `enabled`
// gates the strip; `presets` toggle the stat-driven slogans ("N active
// listings", "N agents", etc.); `customs` are author-supplied strings
// shown after the presets in order. Field shape matches the legacy doc
// schema so all reader code keeps working.
export const BannerSlogansSchema = z.object({
  enabled: z.boolean(),
  presets: z.object({
    activeListings: z.boolean(),
    agents: z.boolean(),
    listingsForSale: z.boolean(),
    listingsForRent: z.boolean(),
  }),
  customs: z.array(z.string()),
});
export type BannerSlogans = z.infer<typeof BannerSlogansSchema>;

// Settings for a single custom (non-agency-default) proptree — the "affiliate"
// case. Returned by GET /api/proptrees/affiliates/:id. The field set mirrors
// the kept-from-agency subset (identity, contact, listings, external, buttons)
// plus the custom-only manual listing picker (`chooseListings` + `listings`).
// Sections we deliberately drop vs the agency page: agency banner / agent feed
// / sponsored buttons / agents tab / showAllAgencyListings / banner slogans /
// newsletter — none of those apply to a single affiliate page.
export const AffiliateSettingsSchema = z.object({
  _id: z.string(),
  enterprise: z.string().nullable(),
  alias: z.string().nullable(),
  shortId: z.string().nullable(),
  status: z.string().nullable(),
  profileImage: z.string().nullable(),
  profileFirstName: z.string().nullable(),
  profileSurName: z.string().nullable(),
  profileCompany: z.string().nullable(),
  // External access toggle.
  externalAccess: ProptreeEnumValueSchema.nullable(),
  // Contact section.
  whatsAppCountryCode: ProptreeEnumValueSchema.nullable(),
  whatsAppNumber: z.string().nullable(),
  // Listings section.
  mainFeed: ProptreeEnumValueSchema.nullable(),
  propertiesType: ProptreeEnumValueSchema.nullable(),
  property: ProptreeEnumValueSchema.nullable(),
  feed: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .nullable(),
  // Custom-only manual listing picker. `chooseListings` is the on/off
  // switch; `listings` is the legacy `{ [externalId]: true }` map of
  // picked listings — only meaningful when `chooseListings.value` is
  // 'enabled'.
  chooseListings: ProptreeEnumValueSchema.nullable(),
  listings: z.record(z.boolean()).nullable(),
  // Action buttons — same shape as agency/agent proptrees.
  buttons: z.array(z.lazy(() => ProfileButtonSchema)),
  // Branded partner toggles (Mortgage Finder, etc.). Top-level field
  // matching the agency-page convention — keys are partner slugs,
  // values are enabled flags. `null` when no partner has been touched.
  partners: z.record(z.boolean()).nullable(),
  // Top-marquee slogan config. Default = `{ enabled: false, presets: …
  // (all false), customs: [] }` on a fresh doc; the GET serializer fills
  // in defaults so the editor always renders.
  bannerSlogans: BannerSlogansSchema,
  // Between-listings decorative card. Same legacy enum-shape as the
  // agency page; `null` when the user has never touched it.
  showAgencyBanner: ProptreeEnumValueSchema.nullable(),
  agencyBannerStats: ProptreeEnumValueSchema.nullable(),
  // Bottom marquee that lists every agent in the brokerage. Same field
  // name and enum-shape as the agency page so the landing renderer can
  // treat both proptree types identically.
  showAgentFeed: ProptreeEnumValueSchema.nullable(),
  // "Stay in the loop" newsletter signup card on the public page.
  newsletter: z.boolean().optional().nullable(),
});
export type AffiliateSettings = z.infer<typeof AffiliateSettingsSchema>;

// Partial-update payload for PUT /api/proptrees/affiliates/:id. Every
// field is optional — the route applies a strict `$set` patch using only
// the keys the caller actually sent, so the client can save one section
// at a time. The first slice wired up here covers the identity fields;
// later commits will extend this schema as more sections come online.
//
// `alias`: lowercased letters / digits / dashes only, or an empty string to
// clear it. The route enforces case-insensitive uniqueness against the
// rest of the enterprise via `aliasLowerCase`.
export const AffiliateSettingsUpdateInputSchema = z.object({
  alias: z
    .string()
    .regex(/^[a-z0-9-]*$/, 'alias may only contain lowercase letters, digits, and dashes')
    .max(60)
    .optional(),
  profileImage: z.string().max(1024).nullable().optional(),
  profileFirstName: z.string().max(120).nullable().optional(),
  profileSurName: z.string().max(120).nullable().optional(),
  profileCompany: z.string().max(120).nullable().optional(),
  // Action buttons — full-array replace (matches the agent-PUT semantics
  // in apps/api/src/routes/agents.ts). The route mints an id on any new
  // entries before saving, so the client can send buttons with `id`
  // unset for newly added rows. Capped at 20 to match the legacy UI.
  profileButtons: z
    .array(z.lazy(() => ProfileButtonInputSchema))
    .max(20)
    .optional(),
  // Legacy enum-shaped switches. Stored as `{ id, name, value }` and
  // round-tripped unchanged so reader code keeps the same dropdown
  // semantics. `null` clears the value entirely.
  externalAccess: ProptreeEnumValueSchema.nullable().optional(),
  whatsAppCountryCode: ProptreeEnumValueSchema.nullable().optional(),
  whatsAppNumber: z.string().max(40).nullable().optional(),
  // Listings filters. `mainFeed` is the master on/off; `propertiesType`
  // and `property` are subsidiary enum picks; `feed` is the linked Form
  // (lead-capture) doc — legacy stores `{ id, name }` so we keep that
  // shape over the wire.
  mainFeed: ProptreeEnumValueSchema.nullable().optional(),
  propertiesType: ProptreeEnumValueSchema.nullable().optional(),
  property: ProptreeEnumValueSchema.nullable().optional(),
  feed: z
    .object({ id: z.string(), name: z.string() })
    .nullable()
    .optional(),
  // Custom-only manual listing picker. `chooseListings` is the on/off
  // switch (legacy enum shape); `listings` is the legacy
  // `{ [externalId]: true }` map of picked listings — only meaningful
  // when `chooseListings.value === 'enabled'`. `null` clears the map.
  chooseListings: ProptreeEnumValueSchema.nullable().optional(),
  listings: z.record(z.boolean()).nullable().optional(),
  // Branded partner toggles. `null` clears the whole map; sending only
  // a subset still does a full-map replace (route enforces).
  partners: z.record(z.boolean()).nullable().optional(),
  // Top-marquee slogan config. Full-shape replace at the doc level.
  bannerSlogans: BannerSlogansSchema.optional(),
  // Decorative agency-banner card + its stats line + newsletter card.
  showAgencyBanner: ProptreeEnumValueSchema.nullable().optional(),
  agencyBannerStats: ProptreeEnumValueSchema.nullable().optional(),
  showAgentFeed: ProptreeEnumValueSchema.nullable().optional(),
  newsletter: z.boolean().optional(),
});
export type AffiliateSettingsUpdateInput = z.infer<
  typeof AffiliateSettingsUpdateInputSchema
>;

// Agent-proptree settings — stored under `agents.*` on the same
// agency-default doc as AgencyProptreeSettings, but conceptually the
// configuration the manager uses to control how every agent page
// inherits agency-level decoration. Mirrors the "Agent links" tab from
// legacy enterprise-app/src/components/createNewProptree.js — keys
// match legacy verbatim where they exist (showAgencyBanner / Stats /
// agencyButtons / showAllAgencyListings) and are extended with the
// new banner-slogan / partners blocks introduced for the new app.
export const AgentsProptreeSettingsSchema = z.object({
  // Master switches that mirror the agency-page block names.
  showAgencyBanner: ProptreeEnumValueSchema.nullable(),
  agencyBannerStats: ProptreeEnumValueSchema.nullable(),
  // Cross-agents marquee (the strip showing other agents on each
  // agent's page). Stored separately from the agency-page version so
  // agencies can keep the agency page packed but quiet the agent
  // pages, or vice versa.
  showAgentFeed: ProptreeEnumValueSchema.nullable(),
  // Listings filter applied to agent pages: include all enterprise
  // listings or only the agent's own. Legacy used the same key.
  showAllAgencyListings: ProptreeEnumValueSchema.nullable(),
  // Agency-controlled buttons rendered after the agent's own buttons
  // on every agent page. Same `ProfileButton` shape, same per-button
  // enabled flag.
  agencyButtons: z.array(z.lazy(() => ProfileButtonSchema)),
  // Branded partner toggles, separate from the agency page's set so
  // the agency can run different partners on agent pages.
  partners: z.record(z.string(), z.boolean()),
  // Top marquee config. Same shape as the agency page's
  // `bannerSlogans`, but with its own enabled / preset / customs so
  // the manager can run a different message on agent pages.
  bannerSlogans: z.object({
    enabled: z.boolean(),
    presets: z.object({
      activeListings: z.boolean(),
      agents: z.boolean(),
      listingsForSale: z.boolean(),
      listingsForRent: z.boolean(),
    }),
    customs: z.array(z.string()),
  }),
  // "Stay in the loop" newsletter card on agent pages — its own switch
  // and copy, separate from the agency page's. `null` copy renders the
  // NEWSLETTER_DEFAULT_* strings.
  newsletter: z.boolean(),
  newsletterTitle: z.string().max(NEWSLETTER_TITLE_MAX).nullable(),
  newsletterSubtitle: z.string().max(NEWSLETTER_SUBTITLE_MAX).nullable(),
});
export type AgentsProptreeSettings = z.infer<
  typeof AgentsProptreeSettingsSchema
>;

// Picker payload returned alongside agent settings — lets the manager
// preview iframe load any one of the agency's agents without a second
// round-trip.
export const AgentsProptreePickerItemSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  alias: z.string().nullable(),
  shortId: z.string().nullable(),
  image: z.string().nullable(),
});
export type AgentsProptreePickerItem = z.infer<
  typeof AgentsProptreePickerItemSchema
>;

export const AgentsProptreeResponseSchema = z.object({
  // The agency-default doc id — used so a future PUT can scope the
  // write to the same doc the agency-settings PUT will eventually
  // target.
  _id: z.string(),
  enterprise: z.string().nullable(),
  // The settings block the manager is editing.
  settings: AgentsProptreeSettingsSchema,
  // Available agent proptrees the user can preview. Empty array if
  // the agency has no agents yet — the manager UI shows an empty
  // state in that case.
  agents: z.array(AgentsProptreePickerItemSchema),
});
export type AgentsProptreeResponse = z.infer<
  typeof AgentsProptreeResponseSchema
>;

// Partial update body for PUT /api/proptrees/agency/agents. Only the
// keys the manager actually changed need be present — the API merges
// them onto the agency-default doc.
export const AgentsProptreeSettingsUpdateSchema =
  AgentsProptreeSettingsSchema.partial();
export type AgentsProptreeSettingsUpdateInput = z.infer<
  typeof AgentsProptreeSettingsUpdateSchema
>;

export const ProptreeListResponseSchema = z.object({
  items: z.array(ProptreeWithAgentSchema),
  // The brokerage's own proptree, returned alongside the agent rows so
  // the manager can pin it above the table regardless of sort or page.
  // Null when the enterprise has no agency-default doc yet.
  agency: ProptreeWithAgentSchema.nullable().optional(),
  meta: z.object({
    total: z.number(),
    page: z.number(),
    limit: z.number(),
    pages: z.number(),
    counts: z.object({
      all: z.number(),
      enabled: z.number(),
      disabled: z.number(),
      pending: z.number(),
      notcreated: z.number(),
    }),
  }),
});
export type ProptreeListResponse = z.infer<typeof ProptreeListResponseSchema>;

export const ProptreeListQuerySchema = z.object({
  status: z
    .enum(['all', 'enabled', 'disabled', 'pending', 'notcreated'])
    .optional(),
  sort: z
    .enum([
      'createdAt',
      'alias',
      'inquiries',
      'agent',
      'activeListings',
      'pageViews',
      'listingClicks',
      'listingInquiries',
      'activeButtons',
      'buttonClicks',
    ])
    .optional(),
  order: z.enum(['asc', 'desc']).optional(),
  page: z.number().int().positive().optional(),
  limit: z.number().int().positive().optional(),
});
export type ProptreeListQuery = z.infer<typeof ProptreeListQuerySchema>;

// Forms (lead-capture forms attached to proptrees). Field shapes are
// intentionally loose — legacy stores everything as plain Object/Array
// with no validated sub-schema for questions, so we passthrough unknowns
// to keep the 153 production docs round-trippable. A stricter
// QuestionField discriminator can be layered on later when we own the
// builder UI end-to-end.
export const FormQuestionOptionSchema = z
  .object({
    id: z.union([z.string(), z.number()]).optional(),
    title: z.string().optional(),
    // Legacy field — flags an option as "qualifying" so the lead is
    // marked as matched when chosen. Defaults to false.
    qualification: z.boolean().optional(),
  })
  .passthrough();

export const FormQuestionSchema = z
  .object({
    id: z.union([z.string(), z.number()]).optional(),
    question: z.string().optional(),
    information: z.string().optional(),
    options: z.array(FormQuestionOptionSchema).optional(),
    // How visitors answer this question. Defaults to radio buttons
    // ("options") when missing — matches the historical behaviour
    // before this field existed. Legacy used a 2-value subset ("input"
    // vs "dropdown") for button-form flows; we generalise to 5 modes.
    // Stored as the standard enum-like shape.
    answerType: ProptreeEnumValueSchema.optional(),
  })
  .passthrough();
export type FormQuestion = z.infer<typeof FormQuestionSchema>;
export type FormQuestionOption = z.infer<typeof FormQuestionOptionSchema>;

// A single proptree a form is connected to. `label` is the proptree's
// alias (or display name / shortId fallback) for the hover list.
export const FormConnectionRefSchema = z.object({
  id: z.string(),
  label: z.string(),
});
export type FormConnectionRef = z.infer<typeof FormConnectionRefSchema>;

// The proptrees a form is used by, split by kind so the UI can show
// "Agency", "Agents · N", "Affiliates · N" chips.
export const FormConnectionsSchema = z.object({
  agency: z.array(FormConnectionRefSchema),
  agents: z.array(FormConnectionRefSchema),
  affiliates: z.array(FormConnectionRefSchema),
});
export type FormConnections = z.infer<typeof FormConnectionsSchema>;

export const FormSchema = z
  .object({
    _id: z.string(),
    enterprise: z.string().nullable(),
    name: z.string().nullable(),
    disabled: z.boolean().nullable(),
    questions: z.array(FormQuestionSchema),
    type: ProptreeEnumValueSchema.nullable(),
    category: ProptreeEnumValueSchema.nullable(),
    theme: ProptreeEnumValueSchema.nullable(),
    language: ProptreeEnumValueSchema.nullable(),
    color: z.string().nullable(),
    qrColor: z.string().nullable(),
    zapierWebhook: z.string().nullable(),
    // New top-level field added by the new manager. Replaces the
    // legacy `category` dropdown which real production forms never
    // actually set. Values: 'whatsapp_qr' | 'sms' | 'email' | 'zapier'.
    qualificationType: ProptreeEnumValueSchema.nullable(),
    // How the form is paginated for the visitor. 'multi_page' (default)
    // is one question per page with a progress bar; 'single_page' lays
    // every question out on one page with a single submit. Defaults to
    // multi_page when missing — matches the legacy behaviour and the
    // expectation in apps/landing/app/form/[id]/page.tsx.
    layout: ProptreeEnumValueSchema.nullable(),
    // Which channels a visitor may verify over, for the '2fa'
    // qualification type. Absent on legacy docs, which the API fills in
    // as all-true — that was the behaviour before the switches existed.
    verificationChannels: z
      .object({
        whatsapp: z.boolean(),
        sms: z.boolean(),
        email: z.boolean(),
      })
      .optional(),
    createdAt: z.union([z.string(), z.date()]).nullable(),
    // Where this form is used, computed by the list endpoint only
    // (omitted on single-form GET / create / update responses). The
    // agency-default proptree's feed.id is the enterprise-wide inquiry
    // form — agent + affiliate pages fall through to it — so that one
    // form lists the agency proptree plus every agent/affiliate page.
    connectedTo: FormConnectionsSchema.optional(),
    // Visitor funnel for the form: page views → first answer → submitted.
    // Optional until the API aggregates it from tracking data; test-mode
    // fixtures seed it, and the forms list renders "—" when absent.
    stats: z
      .object({
        opened: z.number(),
        started: z.number(),
        finished: z.number(),
      })
      .nullable()
      .optional(),
  })
  .passthrough();
export type Form = z.infer<typeof FormSchema>;

export const FormListResponseSchema = z.object({
  items: z.array(FormSchema),
  meta: z.object({
    total: z.number(),
    page: z.number(),
    limit: z.number(),
    pages: z.number(),
    counts: z.object({
      all: z.number(),
      active: z.number(),
      disabled: z.number(),
    }),
  }),
});
export type FormListResponse = z.infer<typeof FormListResponseSchema>;

export const FormListQuerySchema = z.object({
  status: z.enum(['all', 'active', 'disabled']).optional(),
  search: z.string().max(255).optional(),
  sort: z.enum(['createdAt', 'name']).optional(),
  order: z.enum(['asc', 'desc']).optional(),
  // Coerced because Fastify hands `req.query` values in as strings even
  // when the api-client typed them as numbers.
  page: z.coerce.number().int().positive().optional(),
  limit: z.coerce.number().int().positive().max(200).optional(),
});
export type FormListQuery = z.infer<typeof FormListQuerySchema>;

export const FormCreateInputSchema = z.object({
  name: z.string().min(1, 'name required').max(255),
  questions: z.array(FormQuestionSchema).optional(),
  type: ProptreeEnumValueSchema.optional(),
  category: ProptreeEnumValueSchema.optional(),
  theme: ProptreeEnumValueSchema.optional(),
  language: ProptreeEnumValueSchema.optional(),
  color: z.string().max(64).optional(),
  qrColor: z.string().max(64).optional(),
  zapierWebhook: z.string().max(2048).optional(),
  qualificationType: ProptreeEnumValueSchema.optional(),
  layout: ProptreeEnumValueSchema.optional(),
  // Which channels a visitor may verify over, when qualificationType is
  // '2fa'. Independent switches rather than one choice: a brokerage
  // whose leads arrive on WhatsApp still wants email as a fallback for
  // anyone who doesn't use it.
  //
  // Ignored entirely for the other qualification types — 'none' verifies
  // nothing and 'zapier' posts the submission onward.
  verificationChannels: z
    .object({
      whatsapp: z.boolean(),
      sms: z.boolean(),
      email: z.boolean(),
    })
    .optional(),
});
export type FormCreateInput = z.infer<typeof FormCreateInputSchema>;

// Update is a strict patch — same fields, all optional. The route only
// $sets keys that were sent, so omitting a field leaves the doc value
// untouched.
export const FormUpdateInputSchema = FormCreateInputSchema.partial();
export type FormUpdateInput = z.infer<typeof FormUpdateInputSchema>;

// === Lead management ================================================
// Where a brokerage's captured leads get routed. Stored on the
// enterprise doc as `leadRouting`. Each channel is an independent
// switch (several can be on at once); the value fields are only
// meaningful when the matching `enabled` is true. Read/written by the
// manager CRM-settings page via /api/enterprise/lead-routing.
export const LeadRoutingSchema = z.object({
  // Push each lead into the connected CRM (Pixxi). Auto-enabled when a
  // CRM connection is saved; can only be on while a connection exists.
  toCrm: z.boolean(),
  // Forward each lead to the WhatsApp number of the agent who owns the
  // listing/proptree it came from.
  toAgentWhatsapp: z.boolean(),
  // One central WhatsApp number for the whole brokerage.
  centralWhatsapp: z.object({
    enabled: z.boolean(),
    number: z.string().max(40),
  }),
  // One central inbox.
  centralEmail: z.object({
    enabled: z.boolean(),
    email: z.string().max(320),
  }),
  // POST each lead to a custom webhook (Zapier, CRM, in-house, …).
  endpoint: z.object({
    enabled: z.boolean(),
    url: z.string().max(2048),
  }),
});
export type LeadRouting = z.infer<typeof LeadRoutingSchema>;

// Everything off / empty. Used as the default when an enterprise has
// never configured lead routing.
export const DEFAULT_LEAD_ROUTING: LeadRouting = {
  toCrm: false,
  toAgentWhatsapp: false,
  centralWhatsapp: { enabled: false, number: '' },
  centralEmail: { enabled: false, email: '' },
  endpoint: { enabled: false, url: '' },
};

// === CRM connections ================================================
// A brokerage can wire up multiple CRM feeds, each labelled by which
// part of the market it covers. Stored on the enterprise doc as
// `crmConnections`. The first one is seeded from the legacy single-CRM
// fields (crm/crmSource/apiKey/apiSecret) the first time the settings
// page is opened, so existing brokerages keep their connection.
export const CrmConnectionLabelSchema = z.enum([
  'off_plan',
  'on_plan',
  'both',
]);
export type CrmConnectionLabel = z.infer<typeof CrmConnectionLabelSchema>;

// Pixxi comes as two feeds, as in legacy: `pixxi` is the units feed
// (SELL + RENT, "Pixxi CRM – UNITS" / parserType pixxi_unit) and
// `pixxi_project` is the off-plan projects feed (NEW, "Pixxi CRM –
// PROJECTS" / pixxi_project), one listing per project.
export const CrmConnectionTypeSchema = z.enum([
  'propspace',
  'pixxi',
  'pixxi_project',
  'propfusion',
  'propdata',
  'googlesheet',
  // A brokerage-specific feed parser (customParser picks which).
  'custom',
  'goyzer',
  'propertyfinder',
  'other',
]);
export type CrmConnectionType = z.infer<typeof CrmConnectionTypeSchema>;

export function isPixxiType(type: CrmConnectionType): boolean {
  return type === 'pixxi' || type === 'pixxi_project';
}

// What a connection supplies. Units can be ready or off-plan (primary or
// secondary) per listing; projects are developments, always off-plan
// primary. A "mixed" brokerage is one with both kinds of connection —
// e.g. PropSpace for units plus Pixxi or a sheet for projects.
export const CrmConnectionKindSchema = z.enum(['units', 'projects']);
export type CrmConnectionKind = z.infer<typeof CrmConnectionKindSchema>;

export function connectionKind(conn: {
  type: CrmConnectionType;
  kind?: CrmConnectionKind;
}): CrmConnectionKind {
  // Pixxi's two feeds are units and projects by definition.
  if (conn.type === 'pixxi_project') return 'projects';
  if (conn.type === 'pixxi') return 'units';
  return conn.kind ?? 'units';
}

// Feed parsers written for one brokerage's own export. They aren't offered
// to everyone: admin enables them per agency (enterprise.customParsers),
// and an agency whose legacy datasource was one keeps it by default.
// `ready` = ported to the v2 sync; the rest show as coming soon.
export interface CustomParserInfo {
  key: string;
  label: string;
  /** Legacy parserId / enterprise name that selected this parser. */
  legacyIds: string[];
  ready: boolean;
}

export const CUSTOM_PARSERS: readonly CustomParserInfo[] = [
  { key: 'mccone', label: 'McCone feed', legacyIds: ['mccone'], ready: true },
  { key: 'colliers_dubai', label: 'Colliers Dubai feed', legacyIds: ['colliers dubai'], ready: false },
  { key: 'novvi', label: 'NOVVI feed', legacyIds: ['novvi'], ready: false },
  { key: 'edwards_towers', label: 'Edwards Towers feed', legacyIds: ['edwards towers'], ready: false },
  { key: 'unique', label: 'Unique feed', legacyIds: ['unique'], ready: false },
  { key: 'pam_golding_uae', label: 'Pam Golding UAE feed', legacyIds: ['pam golding uae'], ready: false },
];

export function findCustomParser(key: string | null | undefined): CustomParserInfo | undefined {
  return CUSTOM_PARSERS.find((p) => p.key === key);
}

/** The custom parser a legacy datasource used, if any. */
export function legacyCustomParser(doc: {
  parserType?: { value?: unknown } | null;
  parserId?: unknown;
  name?: unknown;
}): string | null {
  const pt = typeof doc.parserType?.value === 'string' ? doc.parserType.value : '';
  if (pt !== 'xml' && pt !== 'xml_project') return null;
  const ids = [doc.parserId, doc.name]
    .filter((v): v is string => typeof v === 'string')
    .map((v) => v.trim().toLowerCase());
  return CUSTOM_PARSERS.find((p) => p.legacyIds.some((l) => ids.includes(l)))?.key ?? null;
}

/**
 * The custom parsers an agency may use: the list admin set, or — never
 * set — the one its legacy datasource used.
 */
export function allowedCustomParsers(doc: {
  customParsers?: unknown;
  parserType?: { value?: unknown } | null;
  parserId?: unknown;
  name?: unknown;
}): string[] {
  if (Array.isArray(doc.customParsers)) {
    return doc.customParsers.filter((k): k is string => typeof k === 'string' && !!findCustomParser(k));
  }
  const legacy = legacyCustomParser(doc);
  return legacy ? [legacy] : [];
}

const priceLimit = z.number().positive().max(1e12);
export const PriceFilterSchema = z.object({
  minSale: priceLimit.optional(),
  maxSale: priceLimit.optional(),
  minRent: priceLimit.optional(),
  maxRent: priceLimit.optional(),
});
export type PriceFilter = z.infer<typeof PriceFilterSchema>;

export const CrmConnectionSchema = z.object({
  // Client-generated stable id (used as the React key + remove target).
  id: z.string().min(1).max(64),
  // Superseded by `kind` in the UI; kept so stored connections and sync
  // logs stay valid.
  label: CrmConnectionLabelSchema,
  type: CrmConnectionTypeSchema,
  // Units or projects — see connectionKind(). Missing = units.
  kind: CrmConnectionKindSchema.optional(),
  // type 'custom': which brokerage parser (CUSTOM_PARSERS key).
  customParser: z.string().max(64).optional(),
  // Only for type 'other'.
  name: z.string().max(100).optional(),
  // Feed-based CRMs (propspace/goyzer/other): the feed URL. Pixxi (both
  // types): the brokerage's company name in Pixxi — the same value the
  // legacy datasource kept in `xmlUrl`.
  sourceUrl: z.string().max(2048).optional(),
  // Property Finder.
  apiKey: z.string().max(512).optional(),
  apiSecret: z.string().max(512).optional(),
  // Pixxi — the X-PIXXI-TOKEN Data-API token (Pixxi CRM → Admin →
  // Integrations → Pixxi Forms).
  apiToken: z.string().max(512).optional(),
  // Propfusion — the brokerage's lead webhook (…propfusion.io/integration/
  // webhook/…), legacy `propfusionURL`. Leads are POSTed there.
  leadUrl: z.string().max(2048).optional(),
  // Goyzer — what its ContactInsert2 lead API needs (legacy goyzer*
  // enterprise fields). Access + group code are the credentials; the
  // others are Goyzer lookup IDs with legacy's defaults when blank.
  goyzer: z
    .object({
      accessCode: z.string().max(256).optional(),
      groupCode: z.string().max(256).optional(),
      mediaName: z.string().max(64).optional(),
      methodOfContact: z.string().max(64).optional(),
      fallbackBrokerId: z.string().max(64).optional(),
      // Refer every lead to the fallback agent instead of the listing's
      // agent. Off by default; any Goyzer brokerage can switch it on.
      alwaysFallback: z.boolean().optional(),
    })
    .optional(),
  // PropData — an API vendor login (Basic auth → a token per site). siteId
  // picks the site when the account reaches several; branchId is where
  // leads go when the enquired listing has no branch of its own.
  propdata: z
    .object({
      username: z.string().max(256).optional(),
      password: z.string().max(512).optional(),
      siteId: z.string().max(64).optional(),
      branchId: z.string().max(64).optional(),
      environment: z.enum(['production', 'staging']).optional(),
    })
    .optional(),
  // Optional price limits, AED (legacy enterprise minPriceSale / …). A
  // listing outside them is held back with the reason; rents compare
  // per year.
  priceFilter: PriceFilterSchema.optional(),
});
export type CrmConnection = z.infer<typeof CrmConnectionSchema>;

export const CrmConnectionsSchema = z.array(CrmConnectionSchema).max(20);
export type CrmConnections = z.infer<typeof CrmConnectionsSchema>;

// === Listing-sync logs ==============================================
// One doc per syncEnterprise() run, stored in the `syncLogs` collection.
// Roll-up totals for the run + per-connection breakdown + an embedded
// per-listing changes array (capped so doc size stays bounded).
export const ChangeKindSchema = z.enum([
  'new',
  'updated',
  'removed',
  'not_supported',
]);
export type ChangeKind = z.infer<typeof ChangeKindSchema>;

export const SyncCountsSchema = z.object({
  new: z.number().int().nonnegative(),
  updated: z.number().int().nonnegative(),
  removed: z.number().int().nonnegative(),
  notSupported: z.number().int().nonnegative(),
  errors: z.number().int().nonnegative(),
});
export type SyncCounts = z.infer<typeof SyncCountsSchema>;

// One block per CrmConnection processed in the run.
export const SyncLogConnectionSchema = z.object({
  connectionId: z.string(),
  type: CrmConnectionTypeSchema,
  label: CrmConnectionLabelSchema,
  counts: SyncCountsSchema,
  durationMs: z.number().int().nonnegative(),
  // Set when the whole connection's fetch/parse failed.
  error: z.string().nullable().optional(),
});
export type SyncLogConnection = z.infer<typeof SyncLogConnectionSchema>;

// Per-listing change. `diff` only set when kind === 'updated' — only the
// fields the orchestrator monitors. `reason` only set for 'not_supported'.
export const SyncLogChangeSchema = z.object({
  connectionId: z.string(),
  externalId: z.string(),
  kind: ChangeKindSchema,
  diff: z
    .object({
      price: z
        .object({ from: z.number().nullable(), to: z.number() })
        .optional(),
      permitId: z
        .object({ from: z.string().nullable(), to: z.string() })
        .optional(),
      broker: z
        .object({ from: z.string().nullable(), to: z.string() })
        .optional(),
    })
    .optional(),
  reason: z.string().optional(),
});
export type SyncLogChange = z.infer<typeof SyncLogChangeSchema>;

export const SyncLogStatusSchema = z.enum(['success', 'partial', 'failed']);
export type SyncLogStatus = z.infer<typeof SyncLogStatusSchema>;

export const SyncLogTriggerSchema = z.enum(['cron', 'manual']);
export type SyncLogTrigger = z.infer<typeof SyncLogTriggerSchema>;

export const SyncLogSchema = z.object({
  _id: z.string(),
  enterprise: z.string(),
  startedAt: z.union([z.string(), z.date()]),
  finishedAt: z.union([z.string(), z.date()]),
  durationMs: z.number().int().nonnegative(),
  status: SyncLogStatusSchema,
  trigger: SyncLogTriggerSchema,
  totals: SyncCountsSchema,
  connections: z.array(SyncLogConnectionSchema),
  changes: z.array(SyncLogChangeSchema),
  truncated: z.boolean(),
});
export type SyncLog = z.infer<typeof SyncLogSchema>;

// List view drops the `changes` payload — it can be thousands of entries
// per sync, and the list view only needs the headline + counts. The
// detail endpoint returns the full doc.
export const SyncLogSummarySchema = SyncLogSchema.omit({ changes: true });
export type SyncLogSummary = z.infer<typeof SyncLogSummarySchema>;

export const SyncLogListQuerySchema = z.object({
  page: z.coerce.number().int().positive().optional(),
  limit: z.coerce.number().int().positive().max(200).optional(),
});
export type SyncLogListQuery = z.infer<typeof SyncLogListQuerySchema>;

export const SyncLogListResponseSchema = z.object({
  items: z.array(SyncLogSummarySchema),
  meta: z.object({
    total: z.number(),
    page: z.number(),
    limit: z.number(),
    pages: z.number(),
  }),
});
export type SyncLogListResponse = z.infer<typeof SyncLogListResponseSchema>;

// === Agent transfer requests ========================================
// When the listing-sync upsert sees a phone that already belongs to a
// user on a DIFFERENT enterprise, we don't auto-transfer. Instead we
// stash a TransferRequest. The agent sees a banner on their existing
// dashboard with their (unchanged) proptree's `transferCode`; they
// either give that code to the new agency's admin (who enters it →
// move completes), or dismiss it (stays on the original enterprise).
//
// Phone uniqueness is enforced application-side in upsertAgent (520
// pre-existing cross-enterprise duplicates make a DB unique index a
// non-starter for now).
export const TransferRequestStatusSchema = z.enum([
  'pending',
  'approved',
  'dismissed',
  'cancelled',
]);
export type TransferRequestStatus = z.infer<typeof TransferRequestStatusSchema>;

export const TransferRequestSchema = z.object({
  _id: z.string(),
  fromUser: z.string(), // owner of the existing phone (the original agency)
  fromEnterprise: z.string(),
  toEnterprise: z.string(), // the new agency whose feed triggered this
  toUser: z.string(), // the new (phone-less) user we just upserted on the new agency
  // Snapshot of the proptree's transferCode at detection time — the
  // agent's "share this with the new agency to approve" code.
  transferCode: z.string(),
  phone: z.string(), // for display in the banner / agency CRM row
  status: TransferRequestStatusSchema,
  detectedAt: z.union([z.string(), z.date()]),
  resolvedAt: z.union([z.string(), z.date()]).nullable().optional(),
  // Set on approve (manager) / dismiss (agent).
  resolvedBy: z.string().nullable().optional(),
});
export type TransferRequest = z.infer<typeof TransferRequestSchema>;

// Approve input — the manager enters the code the agent gave them.
export const TransferRequestApproveInputSchema = z.object({
  code: z.string().min(1).max(64),
});
export type TransferRequestApproveInput = z.infer<
  typeof TransferRequestApproveInputSchema
>;

export const TransferRequestListQuerySchema = z.object({
  status: TransferRequestStatusSchema.optional(),
});
export type TransferRequestListQuery = z.infer<
  typeof TransferRequestListQuerySchema
>;

export const TransferRequestListResponseSchema = z.object({
  items: z.array(TransferRequestSchema),
});
export type TransferRequestListResponse = z.infer<
  typeof TransferRequestListResponseSchema
>;

export const ExampleInputSchema = z.object({
  title: z.string().min(1).max(200),
  description: z.string().max(2000).optional(),
});
export const ExampleSchema = ExampleInputSchema.extend({
  _id: z.string(),
  createdAt: z.string().or(z.date()),
  updatedAt: z.string().or(z.date()),
});
export type ExampleInput = z.infer<typeof ExampleInputSchema>;
export type Example = z.infer<typeof ExampleSchema>;

// ---- Agent self-service proptree settings ----
// External shape — friendly enough for the UI; the route translates to
// the legacy proptree doc field names (socialIcon{Platform},
// profileFirstName, *NotificationDisabled, restoreCode, etc.).

export const SOCIAL_PLATFORMS = [
  'instagram',
  'linkedin',
  'facebook',
  'tiktok',
  'youtube',
  'twitter',
  'snapchat',
] as const;
export type SocialPlatform = (typeof SOCIAL_PLATFORMS)[number];

export const SocialLinkSchema = z.object({
  enabled: z.boolean(),
  username: z.string().max(200),
});
export type SocialLink = z.infer<typeof SocialLinkSchema>;

const socialMapShape = Object.fromEntries(
  SOCIAL_PLATFORMS.map((p) => [p, SocialLinkSchema] as const),
) as Record<SocialPlatform, typeof SocialLinkSchema>;
export const SocialLinksSchema = z.object(socialMapShape);
export type SocialLinks = z.infer<typeof SocialLinksSchema>;

export const ProfileButtonSchema = z.object({
  // Optional on input (server mints one on first save); always set on read.
  id: z.string(),
  title: z.string().min(1, 'title required').max(80),
  // Optional secondary line under the title. Shown by the public proptree
  // for richer button rows (matches the landing page's design examples).
  subtitle: z.string().max(160).nullable(),
  // Empty for `gallery` (no destination — opens an in-card slideshow) and
  // optional for `link` (an icon-only call-to-card with no link is fine).
  // For `video`, this holds the video source URL.
  url: z.string().max(2048),
  // Optional emoji shown when no `image` is set. Stored as the emoji
  // grapheme itself (e.g. "🏠"); 16 chars covers any ZWJ sequence.
  icon: z.string().max(16).nullable(),
  image: z.string().nullable(),
  // Multiple images for `gallery` buttons. Stored alongside the single
  // `image` so a gallery can also have a header thumbnail.
  images: z.array(z.string()).nullable(),
  enabled: z.boolean(),
  // 'link' (default), 'video', 'gallery'. String-typed so the API can
  // accept any new variant without a schema bump.
  type: z.string(),
});
export type ProfileButton = z.infer<typeof ProfileButtonSchema>;

// "true" = receive that notification class. Inverted from legacy's
// `*NotificationDisabled: bool` storage; the route handles the flip.
export const ProptreeNotificationsSchema = z.object({
  accountCritical: z.boolean(),
  reminders: z.boolean(),
  reports: z.boolean(),
});
export type ProptreeNotifications = z.infer<typeof ProptreeNotificationsSchema>;

export const AgentProptreeSettingsSchema = z.object({
  _id: z.string(),
  shortId: z.string().nullable(),
  alias: z.string().nullable(),
  profileImage: z.string().nullable(),
  name: z.string().nullable(),
  // Read-only — the brokerage's company name. Surfaced so the UI can
  // render it locked next to the editable fields.
  company: z.string().nullable(),
  // 7-digit zero-padded code, generated once at proptree creation.
  // Read-only in the agent portal — no regenerate endpoint.
  recoveryCode: z.string().nullable(),
  recoveryEmail: z.string().nullable(),
  social: SocialLinksSchema,
  buttons: z.array(ProfileButtonSchema),
  notifications: ProptreeNotificationsSchema,
});
export type AgentProptreeSettings = z.infer<typeof AgentProptreeSettingsSchema>;

// PUT input — every top-level key is optional so the UI can send a full
// snapshot or just one section. Per-button id is also optional (server
// mints one on insert).
export const ProfileButtonInputSchema = z.object({
  id: z.string().optional(),
  title: z.string().min(1, 'title required').max(80),
  subtitle: z.string().max(160).nullable().optional(),
  // url required only for `link` and `video`; the route enforces that.
  url: z.string().max(2048).optional(),
  icon: z.string().max(16).nullable().optional(),
  image: z.string().nullable().optional(),
  images: z.array(z.string()).max(20).nullable().optional(),
  enabled: z.boolean(),
  type: z.string().optional(),
});
export type ProfileButtonInput = z.infer<typeof ProfileButtonInputSchema>;

export const AgentProptreeSettingsInputSchema = z.object({
  alias: z
    .string()
    .min(1, 'alias required')
    .max(30, 'max 30 characters')
    .regex(/^[a-zA-Z0-9_-]+$/, 'letters, digits, _ or - only')
    .optional(),
  name: z.string().max(100).optional(),
  // Set after a successful Cloudinary direct upload. Validated as URL
  // here so a stray bare path can't sneak into the doc.
  profileImage: z
    .union([z.literal(''), z.string().url('must be a valid URL')])
    .optional(),
  // '' explicitly clears the recovery email; non-empty must be a valid
  // email. The UI sends '' when the user blanks the field.
  recoveryEmail: z.union([z.literal(''), z.string().email()]).optional(),
  social: SocialLinksSchema.partial().optional(),
  buttons: z.array(ProfileButtonInputSchema).max(20).optional(),
  notifications: ProptreeNotificationsSchema.partial().optional(),
});
export type AgentProptreeSettingsInput = z.infer<
  typeof AgentProptreeSettingsInputSchema
>;

// Returned by GET /api/agents/me/proptree/check-alias?alias=foo so the
// UI can show a live "available" / "taken" hint while the user types.
// `same` means the input matches the user's current alias — UI treats
// it as available (saving will be a no-op for that field).
export const AliasCheckResponseSchema = z.object({
  available: z.boolean(),
  reason: z.enum(['taken', 'invalid', 'same']).optional(),
});
export type AliasCheckResponse = z.infer<typeof AliasCheckResponseSchema>;

// Short-lived bundle returned by POST /api/agents/me/proptree/image-signature.
// The browser POSTs the file straight to Cloudinary using these — the
// API secret never leaves the server. Signature is single-use (timestamp
// + signed params); valid for ~1 hour per Cloudinary's spec.
export const ImageUploadSignatureSchema = z.object({
  cloudName: z.string(),
  apiKey: z.string(),
  timestamp: z.number().int(),
  folder: z.string(),
  signature: z.string(),
});
export type ImageUploadSignature = z.infer<typeof ImageUploadSignatureSchema>;

// === Partner buttons =================================================

// Partner buttons — branded call-to-actions (e.g. a mortgage broker) that
// admins configure once and brokerages switch on per proptree. A partner
// owns its own form; each submission is POSTed straight to the partner's
// lead endpoint instead of landing in the brokerage's inquiries.
//
// `key` is what proptree docs store in their `{ [key]: boolean }` toggle
// maps (`partners`, `agents.partners`), so it is fixed at creation.

export const PARTNER_KEY_RE = /^[a-z][a-zA-Z0-9]{1,39}$/;
export const PARTNER_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

/** camelCase key from a display name: "Mortgage Finder" → "mortgageFinder". */
export function partnerKeyFromName(name: string): string {
  const words = name
    .normalize('NFKD')
    .replace(/[^a-zA-Z0-9 ]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const key = words
    .map((w, i) =>
      i === 0
        ? w.toLowerCase()
        : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(),
    )
    .join('')
    .replace(/^[0-9]+/, '')
    .slice(0, 40);
  return key;
}

const leadEndpointSchema = z
  .string()
  .trim()
  .max(2048)
  .url('Lead endpoint must be a full URL')
  .refine((u) => /^https?:\/\//i.test(u), 'Lead endpoint must be http(s)');

export const PartnerSchema = z.object({
  _id: z.string(),
  key: z.string(),
  name: z.string(),
  subtitle: z.string().nullable(),
  color: z.string(),
  logo: z.string().nullable(),
  ctaLabel: z.string(),
  leadEndpoint: z.string().nullable(),
  active: z.boolean(),
  formId: z.string().nullable(),
  excludedEnterprises: z.array(z.string()),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
});
export type Partner = z.infer<typeof PartnerSchema>;

// Admin grid row — adds lead counts (all partner leads vs. ones the
// partner endpoint accepted).
export const PartnerListItemSchema = PartnerSchema.extend({
  stats: z.object({ leads: z.number(), delivered: z.number() }),
});
export type PartnerListItem = z.infer<typeof PartnerListItemSchema>;

export const PartnerListResponseSchema = z.object({
  items: z.array(PartnerListItemSchema),
});
export type PartnerListResponse = z.infer<typeof PartnerListResponseSchema>;

// Detail adds the excluded agencies' names so the editor can render
// chips without a lookup per id.
export const PartnerDetailSchema = PartnerSchema.extend({
  excluded: z.array(z.object({ _id: z.string(), name: z.string() })),
});
export type PartnerDetail = z.infer<typeof PartnerDetailSchema>;

export const PartnerCreateInputSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(60),
  key: z
    .string()
    .regex(PARTNER_KEY_RE, 'Key must be camelCase letters and digits')
    .optional(),
  subtitle: z.string().trim().max(120).nullable().optional(),
  color: z.string().regex(PARTNER_COLOR_RE, 'Color must be #RRGGBB'),
  logo: z.string().url().nullable().optional(),
  ctaLabel: z.string().trim().min(1).max(24).optional(),
  leadEndpoint: leadEndpointSchema.nullable().optional(),
});
export type PartnerCreateInput = z.infer<typeof PartnerCreateInputSchema>;

export const PartnerUpdateInputSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(60).optional(),
  subtitle: z.string().trim().max(120).nullable().optional(),
  color: z.string().regex(PARTNER_COLOR_RE, 'Color must be #RRGGBB').optional(),
  logo: z.string().url().nullable().optional(),
  ctaLabel: z.string().trim().min(1).max(24).optional(),
  leadEndpoint: leadEndpointSchema.nullable().optional(),
  active: z.boolean().optional(),
  excludedEnterprises: z
    .array(z.string().regex(/^[a-f\d]{24}$/i))
    .max(1000)
    .optional(),
});
export type PartnerUpdateInput = z.infer<typeof PartnerUpdateInputSchema>;

// The partner's own form. Same question shape as a brokerage form (so the
// public renderer reuses its question flow), but no verification: after
// the questions the visitor gets plain, optional-to-configure email /
// phone inputs, and the lead goes to the partner on submit.
export const PartnerContactFieldsSchema = z.object({
  email: z.boolean(),
  phone: z.boolean(),
});
export type PartnerContactFields = z.infer<typeof PartnerContactFieldsSchema>;

export const PartnerFormSchema = z.object({
  _id: z.string(),
  name: z.string().nullable(),
  questions: z.array(FormQuestionSchema),
  layout: ProptreeEnumValueSchema.nullable(),
  language: ProptreeEnumValueSchema.nullable(),
  contactFields: PartnerContactFieldsSchema,
});
export type PartnerForm = z.infer<typeof PartnerFormSchema>;

export const PartnerFormUpdateInputSchema = z.object({
  questions: z.array(FormQuestionSchema).max(50).optional(),
  layout: ProptreeEnumValueSchema.nullable().optional(),
  language: ProptreeEnumValueSchema.nullable().optional(),
  contactFields: PartnerContactFieldsSchema.optional(),
});
export type PartnerFormUpdateInput = z.infer<
  typeof PartnerFormUpdateInputSchema
>;

// What brokerages (manager toggles) and the public proptree see. Never
// carries the lead endpoint or the exclusion list.
export const PublicPartnerSchema = z.object({
  key: z.string(),
  name: z.string(),
  subtitle: z.string().nullable(),
  color: z.string(),
  logo: z.string().nullable(),
  ctaLabel: z.string(),
  formId: z.string().nullable(),
});
export type PublicPartner = z.infer<typeof PublicPartnerSchema>;

// === Admin users =====================================================
// Admins are `users` with legacy `level.id === 1`. Inviting one creates a
// pending account (status.id 0) carrying a one-time token; the invitee
// sets their own password on the admin app's /accept-invite page, which
// activates the account. They then sign in with password + email OTP.

export const AdminUserSchema = z.object({
  _id: z.string(),
  email: z.string(),
  name: z.string().nullable(),
  // invited = pending, link still valid; expired = pending, link lapsed.
  status: z.enum(['active', 'invited', 'expired']),
  invitedAt: z.string().nullable(),
  invitedBy: z.string().nullable(),
  createdAt: z.string().nullable(),
  // The signed-in admin's own row — can't be removed from here.
  isSelf: z.boolean(),
});
export type AdminUser = z.infer<typeof AdminUserSchema>;

export const AdminUserListResponseSchema = z.object({
  items: z.array(AdminUserSchema),
});
export type AdminUserListResponse = z.infer<typeof AdminUserListResponseSchema>;

export const AdminInviteInputSchema = z.object({
  email: z.string().trim().email('Enter a valid email address'),
  name: z.string().trim().max(80).optional(),
});
export type AdminInviteInput = z.infer<typeof AdminInviteInputSchema>;

/** What the /accept-invite page shows before the password is set. */
export const AdminInviteInfoSchema = z.object({
  email: z.string(),
  name: z.string().nullable(),
  invitedBy: z.string().nullable(),
});
export type AdminInviteInfo = z.infer<typeof AdminInviteInfoSchema>;

export const AcceptAdminInviteInputSchema = z.object({
  token: z.string().min(20),
  password: z.string().min(8, 'min 8 characters').max(200),
  name: z.string().trim().max(80).optional(),
});
export type AcceptAdminInviteInput = z.infer<typeof AcceptAdminInviteInputSchema>;
